import { movies, type Movie } from '@/mocks/movies';

export type SortKey = 'latest' | 'rating' | 'popularity';

export interface FilterOptions {
  genre?: string;
  decade?: string;
  year?: number;
  minRating?: number;
  keyword?: string;
  sort?: SortKey;
}

export function getAllMovies(): Movie[] {
  return movies;
}

export function getMovieById(id: string): Movie | undefined {
  return movies.find((m) => m.id === id);
}

export function getMoviesByIds(ids: string[]): Movie[] {
  return ids
    .map((id) => getMovieById(id))
    .filter((m): m is Movie => Boolean(m));
}

export function getLatestMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => b.year - a.year).slice(0, limit);
}

export function getHotMovies(limit = 6): Movie[] {
  return [...movies].sort((a, b) => b.popularity - a.popularity).slice(0, limit);
}

export function getTopRatedMovies(limit = 6): Movie[] {
  return [...movies].sort((a, b) => b.rating - a.rating).slice(0, limit);
}

export function getAllYears(): number[] {
  const set = new Set(movies.map((m) => m.year));
  return Array.from(set).sort((a, b) => b - a);
}

export function matchKeyword(movie: Movie, keyword: string): boolean {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return true;
  return (
    movie.title.toLowerCase().includes(kw) ||
    movie.synopsis.toLowerCase().includes(kw) ||
    movie.genres.some((g) => g.toLowerCase().includes(kw)) ||
    movie.tags.some((t) => t.toLowerCase().includes(kw)) ||
    movie.cast.some((c) => c.toLowerCase().includes(kw))
  );
}

function applySort(list: Movie[], sort: SortKey): Movie[] {
  const sorted = [...list];
  if (sort === 'rating') sorted.sort((a, b) => b.rating - a.rating);
  else if (sort === 'popularity') sorted.sort((a, b) => b.popularity - a.popularity);
  else sorted.sort((a, b) => b.year - a.year || b.popularity - a.popularity);
  return sorted;
}

export function filterMovies(options: FilterOptions): Movie[] {
  const { genre, decade, year, minRating, keyword, sort = 'latest' } = options;
  let result = movies.filter((m) => {
    if (genre && !m.genres.includes(genre)) return false;
    if (decade) {
      const [start, end] = decade.split('-').map(Number);
      if (m.year < start || m.year > end) return false;
    }
    if (year && m.year !== year) return false;
    if (minRating && m.rating < minRating) return false;
    if (keyword && !matchKeyword(m, keyword)) return false;
    return true;
  });
  result = applySort(result, sort);
  return result;
}

export function paginate<T>(list: T[], page: number, pageSize: number): { items: T[]; totalPages: number; total: number } {
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return { items: list.slice(start, start + pageSize), totalPages, total };
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  const scored = movies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      let score = 0;
      score += m.genres.filter((g) => movie.genres.includes(g)).length * 3;
      score += m.tags.filter((t) => movie.tags.includes(t)).length * 2;
      score += m.cast.filter((c) => movie.cast.includes(c)).length * 4;
      if (m.director === movie.director) score += 2;
      return { movie: m, score };
    })
    .sort((a, b) => b.score - a.score || b.movie.rating - a.movie.rating);
  return scored.slice(0, limit).map((s) => s.movie);
}

export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h <= 0) return `${m} 分钟`;
  return `${h} 小时 ${m} 分钟`;
}

export function getGenreCount(genre: string): number {
  return movies.filter((m) => m.genres.includes(genre)).length;
}